import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { loadAdsense, updateAdsenseConsent } from '@/lib/loadAdsense';

const COOKIE_CONSENT_KEY = 'mnl_cookie_consent';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    updateAdsenseConsent('granted');
    loadAdsense();
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    updateAdsenseConsent('denied');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-in slide-in-from-bottom duration-500"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="container mx-auto max-w-4xl">
        <div className="bg-card border border-border rounded-xl shadow-xl p-4 md:p-6">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 flex-shrink-0">
              <Cookie className="w-6 h-6 text-primary" />
            </div>
            
            <div className="flex-1">
              <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                We use cookies
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                This website uses cookies and similar technologies to analyze traffic, personalize content, and serve targeted advertisements. 
                By clicking "Accept All", you consent to our use of cookies. 
                For more information, see our{' '}
                <a 
                  href="/privacy-policy" 
                  className="text-primary hover:text-primary/80 underline transition-colors"
                >
                  Privacy Policy
                </a>
                {' '}and learn{' '}
                <a 
                  href="https://policies.google.com/technologies/partner-sites" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary/80 underline transition-colors"
                >
                  how Google uses your data
                </a>.
              </p>
              
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleAccept}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  Accept All
                </Button>
                <Button 
                  onClick={handleDecline}
                  variant="outline"
                  className="border-border hover:bg-accent"
                >
                  Decline Non-Essential
                </Button>
              </div>
            </div>
            
            <button 
              onClick={handleDecline}
              className="text-muted-foreground hover:text-foreground transition-colors p-1"
              aria-label="Close cookie consent"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
